import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  resolved: 'light' | 'dark';
}

const THEME_KEY = 'platform_theme';

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? getSystemTheme() : mode;
}

const storedMode = (localStorage.getItem(THEME_KEY) as ThemeMode) || 'light';
const resolved = resolveTheme(storedMode);

const initialState: ThemeState = {
  mode: storedMode,
  resolved,
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<ThemeMode>) => {
      state.mode = action.payload;
      state.resolved = resolveTheme(action.payload);
      localStorage.setItem(THEME_KEY, action.payload);
      document.documentElement.setAttribute('data-theme', state.resolved);
    },
    syncSystemTheme: (state) => {
      if (state.mode === 'system') {
        state.resolved = getSystemTheme();
        document.documentElement.setAttribute('data-theme', state.resolved);
      }
    },
    toggleTheme: (state) => {
      const next = state.resolved === 'light' ? 'dark' : 'light';
      state.mode = next;
      state.resolved = next;
      localStorage.setItem(THEME_KEY, next);
      document.documentElement.setAttribute('data-theme', next);
    },
    initTheme: (state) => {
      document.documentElement.setAttribute('data-theme', state.resolved);
    },
  },
});

export const { setTheme, toggleTheme, initTheme, syncSystemTheme } = themeSlice.actions;
export default themeSlice.reducer;
